import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';

import { listTelemetryEvents, type PortalTelemetryEvent } from '../api.js';

export interface TelemetryPanelProps {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (reads via the session cookie). */
  editToken: string | null;
  /**
   * Customer's current tier. The endpoint accepts any tenant's events
   * regardless of license, but a Free / Pro device's renderer doesn't
   * flush telemetry (the `telemetryExport` feature flag gates the
   * emitter). We show a "Enterprise feature" notice in that case so
   * an empty list doesn't read as "broken".
   */
  currentTier: 'core' | 'pro' | 'enterprise';
}

/**
 * Operational event categories. Deliberately excludes `audit` — privileged
 * admin actions (exit gesture, PIN, overlay access) are an accountability
 * trail with its own dedicated Audit panel, not part of the usage/health
 * telemetry stream. Mirrors src/schema/telemetry.ts.
 */
const CATEGORIES = [
  '',
  'tileLaunch',
  'profileSwitch',
  'configError',
  'licenseError',
  'agentAction',
  'remoteConfigPoll',
  'oem',
] as const;

const REFRESH_INTERVAL_MS = 30_000;
const PAGE_LIMIT = 100;

/**
 * Customer-facing view of recent telemetry events the fleet has flushed.
 *
 * Pure read surface — devices POST to the public ingest endpoint
 * (/telemetry/:tenantId/events) on their own flush schedule, and this
 * panel polls the customer-Bearer-auth view of the same store every
 * 30 seconds. Lets a customer verify the pipeline is working before
 * they wire the endpoint into their own observability stack.
 *
 * Empty state separates "nothing yet" from "you're not entitled":
 * Free / Pro tenants see an upgrade nudge; Enterprise tenants with no
 * events see a "your devices haven't reported in yet" hint.
 */
export function TelemetryPanel({
  tenantId,
  editToken,
  currentTier,
}: TelemetryPanelProps): JSX.Element {
  const [events, setEvents] = useState<PortalTelemetryEvent[]>([]);
  const [category, setCategory] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'unauthorized' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');

  // Client-side text filter over the loaded page (the Category dropdown
  // already narrows server-side). Matches category, event name, and the
  // attrs blob — so a search for an app package or deviceId works.
  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (q === '') return events;
    return events.filter((e) => telemetryEventMatches(e, q));
  }, [events, trimmedQuery]);
  const filtering = trimmedQuery !== '';

  function toggleExpanded(id: number): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const refresh = useCallback(async () => {
    setStatus('loading');
    const opts: { tenantId: string; editToken: string | null; limit: number; category?: string } = {
      tenantId,
      editToken,
      limit: PAGE_LIMIT,
    };
    if (category !== '') opts.category = category;
    const res = await listTelemetryEvents(opts);
    if (res.kind === 'ok') {
      setEvents(res.events);
      setErrorMsg(null);
      setStatus('ok');
    } else if (res.kind === 'unauthorized') {
      setStatus('unauthorized');
      setErrorMsg('Your edit token was rejected — re-open the editor from the Fleets list to refresh it.');
    } else {
      setStatus('error');
      setErrorMsg(
        res.kind === 'network'
          ? `Network error: ${res.message}`
          : `Unexpected ${String(res.status)} response.`,
      );
    }
  }, [tenantId, editToken, category]);

  // Initial load + auto-refresh every 30s. The endpoint is cheap (one
  // indexed SELECT) and devices ship at minute cadence by default,
  // so 30s gives the customer a "live" feel without hammering D1.
  useEffect(() => {
    void refresh();
    const h = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(h);
    };
  }, [refresh]);

  const isEnterprise = currentTier === 'enterprise';

  return (
    <section className="telemetry-panel" data-testid="telemetry-panel">
      <header className="telemetry-panel__head">
        <div className="telemetry-panel__head-text">
          <p className="telemetry-panel__sub">
            Recent events your fleet has flushed to the hosted ingest
            endpoint. Refreshes every 30 seconds. Point{' '}
            <code>telemetry.endpoint</code> at your own collector if you
            want a long-term store — see{' '}
            <a
              href="https://mobilauncher.com/docs/telemetry"
              target="_blank"
              rel="noopener noreferrer"
              className="telemetry-panel__doc-link"
            >
              the docs
            </a>
            .
          </p>
        </div>
        <div className="telemetry-panel__controls">
          <input
            type="search"
            className="telemetry-panel__search"
            placeholder="Search events…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="telemetry-search"
          />
          <label className="telemetry-panel__filter">
            <span className="telemetry-panel__filter-label">Category</span>
            <select
              className="telemetry-panel__filter-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="telemetry-filter"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c === '' ? 'All' : c}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="builder__btn builder__btn--ghost"
            onClick={() => downloadCsv(filtered, tenantId)}
            disabled={filtered.length === 0}
            title={filtered.length === 0 ? 'No events to export yet' : `Export ${String(filtered.length)} events as CSV`}
            data-testid="telemetry-csv"
          >
            ⤓ CSV
          </button>
          <button
            type="button"
            className="builder__btn builder__btn--ghost"
            onClick={() => void refresh()}
            disabled={status === 'loading'}
            data-testid="telemetry-refresh"
          >
            {status === 'loading' ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </header>

      {errorMsg !== null && (
        <p className="telemetry-panel__error" role="alert">
          {errorMsg}
        </p>
      )}

      {events.length === 0 && status === 'ok' && (
        <EmptyState isEnterprise={isEnterprise} hasFilter={category !== ''} />
      )}

      {events.length > 0 && filtered.length === 0 && (
        <p className="telemetry-panel__empty" data-testid="telemetry-search-empty">
          No events match “{trimmedQuery}”.
        </p>
      )}

      {filtered.length > 0 && (
        <table className="telemetry-panel__table" data-testid="telemetry-table">
          <thead>
            <tr>
              <th className="telemetry-panel__col-time">Received</th>
              <th className="telemetry-panel__col-category">Category</th>
              <th className="telemetry-panel__col-event">Event</th>
              <th className="telemetry-panel__col-summary">Summary</th>
              <th className="telemetry-panel__col-device">Device</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const deviceId = extractDeviceId(e.attrs);
              const isOpen = expanded.has(e.id);
              const restAttrs = stripEnvelopeKeys(e.attrs);
              return (
                <tr key={e.id} data-testid="telemetry-row">
                  <td className="telemetry-panel__col-time">
                    <time dateTime={new Date(e.receivedAt).toISOString()}>
                      {formatRelative(e.receivedAt)}
                    </time>
                  </td>
                  <td className="telemetry-panel__col-category">
                    <span className={`telemetry-panel__cat telemetry-panel__cat--${e.category}`}>
                      {e.category}
                    </span>
                  </td>
                  <td className="telemetry-panel__col-event">
                    {formatEvent(e.category, e.name)}
                  </td>
                  <td className="telemetry-panel__col-summary">
                    {restAttrs === null ? (
                      <span className="telemetry-panel__muted">—</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="telemetry-panel__expand"
                          onClick={() => toggleExpanded(e.id)}
                          aria-expanded={isOpen}
                          data-testid="telemetry-row-expand"
                        >
                          {isOpen ? '▾' : '▸'} {summarizeEvent(e.category, e.name, restAttrs)}
                        </button>
                        {isOpen && (
                          <pre className="telemetry-panel__details-pre">
                            {JSON.stringify(restAttrs, null, 2)}
                          </pre>
                        )}
                      </>
                    )}
                  </td>
                  <td className="telemetry-panel__col-device">
                    {deviceId === null ? (
                      <span className="telemetry-panel__muted">—</span>
                    ) : (
                      <code className="telemetry-panel__device" title={deviceId}>
                        {shortDevice(deviceId)}
                      </code>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {filtered.length > 0 && (
        <p className="telemetry-panel__foot">
          {filtering
            ? `Showing ${String(filtered.length)} of ${String(events.length)} loaded events.`
            : `Showing the ${String(events.length)} most recent event${events.length === 1 ? '' : 's'}. Older events age out after ~14 days.`}
        </p>
      )}
    </section>
  );
}

/**
 * Free-text match over a telemetry event: category, event name, and the
 * stringified attrs (so an app package, deviceId, or tile label hits).
 */
function telemetryEventMatches(e: PortalTelemetryEvent, q: string): boolean {
  if (e.category.toLowerCase().includes(q)) return true;
  if (e.name.toLowerCase().includes(q)) return true;
  if (e.attrs !== undefined) {
    try {
      if (JSON.stringify(e.attrs).toLowerCase().includes(q)) return true;
    } catch {
      // non-serializable attrs — skip
    }
  }
  return false;
}

function EmptyState({
  isEnterprise,
  hasFilter,
}: {
  isEnterprise: boolean;
  hasFilter: boolean;
}): JSX.Element {
  if (!isEnterprise) {
    return (
      <div className="telemetry-panel__empty" data-testid="telemetry-empty-locked">
        <p>
          <strong>Telemetry export is an Enterprise feature.</strong> Devices
          on Free and Pro don't flush events to this sink — but your config
          already ships with a working <code>telemetry</code> block, so
          the moment you upgrade your devices start reporting here without
          a config change.
        </p>
      </div>
    );
  }
  if (hasFilter) {
    return (
      <div className="telemetry-panel__empty" data-testid="telemetry-empty-filter">
        <p>
          No events in the selected category yet. Clear the filter to see
          everything your fleet has reported.
        </p>
      </div>
    );
  }
  return (
    <div className="telemetry-panel__empty" data-testid="telemetry-empty">
      <p>
        <strong>No events yet.</strong> Devices flush on a 60-second
        cadence by default, so allow a minute or two after first boot
        for events to land here. Check that{' '}
        <code>telemetry.enabled</code> is true on the Telemetry editor.
      </p>
    </div>
  );
}

/**
 * Friendly per-event display. Picks up the same kinds of name-cleanups
 * the Audit panel does — turns "tile.dispatch" into something a human
 * can scan without remembering our internal naming. Falls through to
 * the raw `name` when the (category, name) pair isn't in the map.
 */
const EVENT_LABELS: Record<string, string> = {
  'tileLaunch:app':              'App launched',
  'tileLaunch:web':              'Web tile opened',
  'tileLaunch:agentAction':      'Agent action',
  'profileSwitch:changed':       'Profile switched',
  'configError:configParseError':'Config parse error',
  'licenseError:expired':        'License expired',
  'licenseError:missing':        'License missing',
  'licenseError:tenantMismatch': 'License/tenant mismatch',
  'remoteConfigPoll:pollSucceeded':   'Remote config polled',
  'remoteConfigPoll:pollNotModified': 'Remote config unchanged',
  'remoteConfigPoll:pollNetworkError':'Remote config network error',
  'remoteConfigPoll:pollServerError': 'Remote config server error',
  'audit:tile.dispatch':         'Tile dispatched',
  'audit:admin.exitGesture':     'Admin exit gesture',
  'audit:admin.pinAccepted':     'Admin PIN accepted',
  'audit:admin.pinRejected':     'Admin PIN rejected',
};

function formatEvent(category: string, name: string): string {
  return EVENT_LABELS[`${category}:${name}`] ?? name;
}

/**
 * One-line, category-aware summary of the most useful attrs fields.
 * The runtime now enriches tile telemetry with `label`, `packageName`,
 * `url`, and `action` (see engine.ts `tileDescriptor`), so the
 * common cases get a readable summary instead of a UUID. Falls back
 * to a generic key=value join.
 */
function summarizeEvent(
  category: string,
  name: string,
  attrs: Record<string, unknown>,
): string {
  if (category === 'tileLaunch' || (category === 'audit' && name === 'tile.dispatch')) {
    const details = unwrapDetails(attrs);
    const label = typeof details['label'] === 'string' ? details['label'] : null;
    const pkg = typeof details['packageName'] === 'string' ? details['packageName'] : null;
    const url = typeof details['url'] === 'string' ? details['url'] : null;
    const action = typeof details['action'] === 'string' ? details['action'] : null;
    const ok = details['ok'];
    const failureNote = ok === false
      ? ` — failed${typeof details['reason'] === 'string' ? ` (${details['reason'] as string})` : ''}`
      : '';
    if (label !== null && pkg !== null) return `${label} (${pkg})${failureNote}`;
    if (label !== null && url !== null) return `${label} → ${truncate(url, 40)}${failureNote}`;
    if (label !== null && action !== null) return `${label} → ${action}${failureNote}`;
    if (label !== null) return `${label}${failureNote}`;
    if (pkg !== null) return `${pkg}${failureNote}`;
    return genericSummary(attrs);
  }
  if (category === 'profileSwitch') {
    const from = typeof attrs['fromName'] === 'string' ? attrs['fromName'] : attrs['fromId'];
    const to = typeof attrs['toName'] === 'string' ? attrs['toName'] : attrs['toId'];
    if (from !== undefined && to !== undefined) return `${String(from)} → ${String(to)}`;
  }
  return genericSummary(attrs);
}

/**
 * Audit details ride one level deep under `details`. Unwrap so the
 * summary picker sees `label` / `packageName` etc. directly instead
 * of the `{ at, details: { label, ... } }` envelope.
 */
function unwrapDetails(attrs: Record<string, unknown>): Record<string, unknown> {
  const inner = attrs['details'];
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return attrs;
}

function genericSummary(attrs: Record<string, unknown>): string {
  const keys = Object.keys(attrs).slice(0, 4);
  if (keys.length === 0) return '(no attrs)';
  return keys
    .map((k) => {
      const v = attrs[k];
      const display =
        typeof v === 'string'
          ? truncate(v, 24)
          : typeof v === 'number' || typeof v === 'boolean'
            ? String(v)
            : '…';
      return `${k}=${display}`;
    })
    .join(', ');
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

/** Pull deviceId from attrs.deviceId or attrs.details.deviceId (audit shape). */
function extractDeviceId(attrs: unknown): string | null {
  if (attrs === null || typeof attrs !== 'object') return null;
  const obj = attrs as Record<string, unknown>;
  const id = obj['deviceId'];
  if (typeof id === 'string' && id.length > 0) return id;
  const inner = obj['details'];
  if (inner !== null && typeof inner === 'object') {
    const innerId = (inner as Record<string, unknown>)['deviceId'];
    if (typeof innerId === 'string' && innerId.length > 0) return innerId;
  }
  return null;
}

/**
 * Drop the envelope keys we display in their own columns (`deviceId`,
 * audit `at`) so the expand-JSON view shows only the meaningful payload.
 * Returns null when nothing's left worth rendering.
 */
function stripEnvelopeKeys(attrs: unknown): Record<string, unknown> | null {
  if (attrs === null || typeof attrs !== 'object') return null;
  const obj = { ...(attrs as Record<string, unknown>) };
  delete obj['deviceId'];
  delete obj['at'];
  return Object.keys(obj).length === 0 ? null : obj;
}

function shortDevice(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-3)}` : id;
}

/**
 * "30s ago", "5m ago", "2h ago", "yesterday". Anything past 7 days
 * falls back to an absolute date. Cheap human-readable diff that
 * doesn't need a date library.
 */
function formatRelative(epochMs: number): string {
  const diffSec = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (diffSec < 60) return `${String(diffSec)}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${String(diffMin)}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${String(diffHr)}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${String(diffDay)} days ago`;
  return new Date(epochMs).toLocaleDateString();
}

/**
 * Dump the current event list as CSV and trigger a download. Used by
 * customers who want to spreadsheet their telemetry — picking through
 * 100 rows is easier in Excel than scrolling the panel. CRLF endings
 * because Excel on Windows is the dominant consumer of this file;
 * Excel handles LF too but CRLF is the safer default.
 */
function downloadCsv(events: PortalTelemetryEvent[], tenantId: string): void {
  const header = ['id', 'received_at_iso', 'device_ts_iso', 'category', 'name', 'attrs'];
  const rows = events.map((e) => [
    String(e.id),
    new Date(e.receivedAt).toISOString(),
    new Date(e.ts).toISOString(),
    e.category,
    e.name,
    e.attrs === undefined ? '' : JSON.stringify(e.attrs),
  ]);
  const csv = [header, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mobilauncher-telemetry-${tenantId}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Wrap a CSV field in double quotes whenever it contains a comma,
 * quote, or newline; escape embedded quotes by doubling. Conservative
 * — also quotes any non-ASCII so Excel's CSV import doesn't choke on
 * UTF-8 BOM heuristics on Windows.
 */
function escapeCsvField(s: string): string {
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
